import { Injectable, inject, signal } from '@angular/core';
import { App } from '@capacitor/app';
import { NativeBiometric, BiometryType } from '@capgo/capacitor-native-biometric';
import { LoggingService } from './logging.service';
import { PlatformService } from './platform.service';

export interface BiometricAvailability {
  isAvailable: boolean;
  biometryType: BiometryType;
}

export interface BiometricCredentials {
  email: string;
  refreshToken: string;
}

const BIOMETRIC_SERVER_FALLBACK = 'com.loandepot.consumerportal';
const BIOMETRIC_ENABLED_KEY = 'biometric_login_enabled';

@Injectable({ providedIn: 'root' })
export class BiometricService {
  private readonly platformService = inject(PlatformService);
  private readonly loggingService = inject(LoggingService);

  private cachedBundleId: string | null = null;

  readonly biometricAvailability = signal<BiometricAvailability>({
    isAvailable: false,
    biometryType: BiometryType.NONE,
  });

  readonly isBiometricEnabled = signal(false);

  /** Runtime-only flag: true once user has reached the login screen in this app session (survives route nav, resets on app kill). */
  biometricAutoLoginDone = false;

  constructor() {
    if (this.platformService.isNativeMobile()) {
      this.checkAvailability();
      this.loadEnabledState();
      this.resolveBundleId();
    }
  }

  private async checkAvailability(): Promise<void> {
    try {
      const result = await NativeBiometric.isAvailable({ useFallback: false });
      this.biometricAvailability.set({
        isAvailable: result.isAvailable,
        biometryType: result.biometryType,
      });
      this.loggingService.logInfo(
        `[BiometricService] checkAvailability: isAvailable=${result.isAvailable} | biometryType=${result.biometryType} (${this.getBiometryLabel()})`,
      );
    } catch (error) {
      this.biometricAvailability.set({
        isAvailable: false,
        biometryType: BiometryType.NONE,
      });
      this.loggingService.logWarning(
        `[BiometricService] checkAvailability: not available | error=${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  private loadEnabledState(): void {
    const enabled = localStorage.getItem(BIOMETRIC_ENABLED_KEY) === 'true';
    this.isBiometricEnabled.set(enabled);
    this.loggingService.logInfo(`[BiometricService] loadEnabledState: biometricEnabled=${enabled}`);
  }

  private async resolveBundleId(): Promise<void> {
    try {
      const info = await App.getInfo();
      this.cachedBundleId = info.id;
      this.loggingService.logInfo(`[BiometricService] resolveBundleId: ${this.cachedBundleId}`);
    } catch (error) {
      this.loggingService.logWarning(
        `[BiometricService] resolveBundleId: failed, using fallback | error=${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  private async getServer(): Promise<string> {
    if (this.cachedBundleId) {
      return this.cachedBundleId;
    }
    try {
      const info = await App.getInfo();
      this.cachedBundleId = info.id;
      return this.cachedBundleId;
    } catch {
      return BIOMETRIC_SERVER_FALLBACK;
    }
  }

  getBiometryLabel(): string {
    const type = this.biometricAvailability().biometryType;
    switch (type) {
      case BiometryType.FACE_ID:
        return 'Face ID';
      case BiometryType.TOUCH_ID:
        return 'Touch ID';
      case BiometryType.FINGERPRINT:
        return 'Fingerprint';
      case BiometryType.FACE_AUTHENTICATION:
        return 'Face Authentication';
      case BiometryType.IRIS_AUTHENTICATION:
        return 'Iris Authentication';
      default:
        return 'Biometrics';
    }
  }

  getBiometryIcon(): string {
    const type = this.biometricAvailability().biometryType;
    switch (type) {
      case BiometryType.FACE_ID:
      case BiometryType.FACE_AUTHENTICATION:
        return 'face';
      case BiometryType.TOUCH_ID:
      case BiometryType.FINGERPRINT:
      default:
        return 'fingerprint';
    }
  }

  async verifyIdentity(reason?: string): Promise<boolean> {
    this.loggingService.logInfo(`[BiometricService] verifyIdentity: prompting user | type=${this.getBiometryLabel()}`);
    try {
      await NativeBiometric.verifyIdentity({
        reason: reason ?? `Sign in with ${this.getBiometryLabel()}`,
        title: 'Authenticate',
        useFallback: this.platformService.isIOS(),
        maxAttempts: 3,
      });
      this.loggingService.logInfo('[BiometricService] verifyIdentity: SUCCESS');
      return true;
    } catch (error) {
      this.loggingService.logWarning(
        `[BiometricService] verifyIdentity: FAILED | error=${error instanceof Error ? error.message : String(error)}`,
      );
      return false;
    }
  }

  async enableBiometricLogin(email: string, refreshToken: string): Promise<boolean> {
    const available = this.biometricAvailability();
    if (!available.isAvailable) {
      this.loggingService.logWarning('[BiometricService] enableBiometricLogin: biometric not available, skipping');
      return false;
    }

    const server = await this.getServer();
    this.loggingService.logInfo(
      `[BiometricService] enableBiometricLogin: storing credentials | email=${email} | refreshTokenLength=${refreshToken.length} | server=${server}`,
    );

    try {
      await NativeBiometric.setCredentials({
        username: email,
        password: refreshToken,
        server,
      });
      localStorage.setItem(BIOMETRIC_ENABLED_KEY, 'true');
      this.isBiometricEnabled.set(true);
      this.loggingService.logInfo('[BiometricService] enableBiometricLogin: SUCCESS, credentials stored in keychain');
      return true;
    } catch (error) {
      this.loggingService.logWarning(
        `[BiometricService] enableBiometricLogin: FAILED | error=${error instanceof Error ? error.message : String(error)}`,
      );
      return false;
    }
  }

  async disableBiometricLogin(): Promise<void> {
    this.loggingService.logInfo('[BiometricService] disableBiometricLogin: clearing credentials from keychain');
    try {
      const server = await this.getServer();
      await NativeBiometric.deleteCredentials({ server });
      this.loggingService.logInfo('[BiometricService] disableBiometricLogin: keychain credentials deleted');
    } catch {
      this.loggingService.logInfo('[BiometricService] disableBiometricLogin: no credentials to delete (already clean)');
    }
    localStorage.removeItem(BIOMETRIC_ENABLED_KEY);
    this.isBiometricEnabled.set(false);
    this.loggingService.logInfo('[BiometricService] disableBiometricLogin: COMPLETE, biometric disabled');
  }

  async getStoredCredentials(): Promise<BiometricCredentials | null> {
    if (!this.isBiometricEnabled()) {
      this.loggingService.logInfo('[BiometricService] getStoredCredentials: biometric not enabled, returning null');
      return null;
    }

    try {
      const server = await this.getServer();
      const credentials = await NativeBiometric.getCredentials({ server });
      if (!credentials.username) {
        this.loggingService.logWarning('[BiometricService] getStoredCredentials: no username in stored credentials');
        return null;
      }
      this.loggingService.logInfo(
        `[BiometricService] getStoredCredentials: retrieved | email=${credentials.username} | hasRefreshToken=${!!credentials.password} | refreshTokenLength=${credentials.password?.length ?? 0}`,
      );
      return {
        email: credentials.username,
        refreshToken: credentials.password,
      };
    } catch (error) {
      this.loggingService.logWarning(
        `[BiometricService] getStoredCredentials: FAILED | error=${error instanceof Error ? error.message : String(error)}`,
      );
      return null;
    }
  }

  async authenticateAndGetCredentials(): Promise<BiometricCredentials | null> {
    this.loggingService.logInfo(
      `[BiometricService] authenticateAndGetCredentials: starting | enabled=${this.isBiometricEnabled()} | available=${this.biometricAvailability().isAvailable}`,
    );

    if (!this.isBiometricEnabled() || !this.biometricAvailability().isAvailable) {
      this.loggingService.logWarning('[BiometricService] authenticateAndGetCredentials: preconditions not met, returning null');
      return null;
    }

    const verified = await this.verifyIdentity();
    if (!verified) {
      this.loggingService.logWarning('[BiometricService] authenticateAndGetCredentials: biometric verification failed');
      return null;
    }

    this.loggingService.logInfo('[BiometricService] authenticateAndGetCredentials: verified, retrieving credentials');
    return this.getStoredCredentials();
  }
}
